// ============================================
// Utility Functions
// ============================================

import { clsx, type ClassValue } from 'clsx';

// Class name utility
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Format date for display
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('ar-IL', {
    timeZone: 'Asia/Jerusalem',
    ...options,
  });
}

// Format time for display
export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('ar-IL', {
    timeZone: 'Asia/Jerusalem',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format datetime
export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('ar-IL', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Relative time
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'الآن';
  if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
  if (diffHours < 24) return `منذ ${diffHours} ساعة`;
  if (diffDays < 7) return `منذ ${diffDays} يوم`;

  return formatDate(d, { month: 'short', day: 'numeric' });
}

// Format currency (ILS)
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Format number
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('ar-SA').format(num);
}

// Format phone for display (already masked)
export function formatPhone(phone: string): string {
  return phone;
}

// Stage labels in Arabic
export const stageLabels: Record<string, string> = {
  new: 'جديد',
  contacted: 'تم التواصل',
  interested: 'مهتم',
  waiting_eligibility: 'انتظار الأهلية',
  approved: 'معتمد',
  rejected: 'مرفوض',
  funded: 'ممول',
  closed_won: 'مغلق - نجاح',
  closed_lost: 'مغلق - خسارة',
};

// Stage colors
export const stageColors: Record<string, string> = {
  new: 'badge-new',
  contacted: 'badge-blue',
  interested: 'badge-amber',
  waiting_eligibility: 'badge-waiting',
  approved: 'badge-approved',
  rejected: 'badge-rejected',
  funded: 'badge-funded',
  closed_won: 'badge-green',
  closed_lost: 'badge-gray',
};

// Project labels
export const projectLabels: Record<string, string> = {
  hot_mobile: 'Hot Mobile',
  adult_store: 'Adult Store',
};

// Project colors
export const projectColors: Record<string, string> = {
  hot_mobile: 'text-orange-400',
  adult_store: 'text-pink-400',
};

// Source labels
export const sourceLabels: Record<string, string> = {
  website: 'الموقع',
  whatsapp: 'واتساب',
  manual: 'يدوي',
  referral: 'إحالة',
};

// Payment method labels
export const paymentMethodLabels: Record<string, string> = {
  bank_transfer: 'تحويل بنكي',
  visa: 'بطاقة ائتمان',
};

// Tier labels
export const tierLabels: Record<string, string> = {
  A: 'Tier A (1-18 قسط)',
  B: 'Tier B (19-36 قسط)',
};

// Copy to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
}

// Debounce
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// Truncate text
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}
